import { useEffect, useMemo, useState } from "react";
import { OrderReceipt } from "../components/types";
import { getApprovalOrderReceipts, getFaApprovedOrderReceipts } from "../states/PicklistAction";
import { Department, OrderType } from "../models";
import { getDates, getISOEndOfDate, getISOStartOfDate } from "../utilities/Format";
import { RECEIPT_STATUSES } from "../pages/OrderReceiptApproval";

export default function useOrderReceipt(
  dateFrom: Date | null,
  dateTo: Date | null,
  department: Department | undefined,
  receiptStatus: RECEIPT_STATUSES,
  searchByOrder: string,
  currentOrderType: OrderType | undefined
) : OrderReceipt[] {
  const [ orderReceipts, setOrderReceipts ] = useState<OrderReceipt[]>([]);

  useEffect(() => {
    if (!dateFrom || !dateTo) return

    const dateFromISO = getISOStartOfDate(dateFrom)
    const dateToISO = getISOEndOfDate(dateTo)
  
    if (department === Department.FA) {
      // query by WH Approved
      if (receiptStatus === RECEIPT_STATUSES.WAIT_FOR_APPROVE) {
        getApprovalOrderReceipts(department, dateFromISO, dateToISO)
          .then(result => {
            setOrderReceipts(result)
          })
          .catch(err => console.error(err))
      } else if (receiptStatus === RECEIPT_STATUSES.APPROVED) {
        const periodDates = getDates(dateFrom, dateTo) // sorted date FIFO (First In First Out)
        Promise.allSettled(periodDates.map(date => {
          return getFaApprovedOrderReceipts(date)
        })).then(results => {
          const totalOrderReceipts = (results
            .filter(res => res.status === 'fulfilled') as PromiseFulfilledResult<OrderReceipt[]>[])
            .reduce((prev, cur) => [...prev, ...cur.value], [] as OrderReceipt[])
          setOrderReceipts(totalOrderReceipts)
        })
        .catch(err => console.error(err))
      }
    } else {
      // query by Current Approver
      if (receiptStatus === RECEIPT_STATUSES.WAIT_FOR_APPROVE) {
        getApprovalOrderReceipts(department, dateFromISO, dateToISO)
          .then(result => {
            setOrderReceipts(result)
          })
          .catch(err => console.error(err))
      } else if (receiptStatus === RECEIPT_STATUSES.APPROVED) {
        const nextDepartmentApprover = getNextApprover(department)
        getApprovalOrderReceipts(nextDepartmentApprover, dateFromISO, dateToISO)
          .then(result => {
            setOrderReceipts(result)
          })
          .catch(err => console.error(err))
      }
    }
  }, [ dateFrom, dateTo, department, receiptStatus ]);

  const filteredTypeReceipts = useMemo(() => {
    if (currentOrderType === undefined) return orderReceipts
    return orderReceipts.filter(rep => rep.orderType === currentOrderType)
  }, [ currentOrderType, orderReceipts ])
  
  const filteredReceipts = useMemo(() => {
    return filteredTypeReceipts.filter(rep => 
      rep.productionOrderId.includes(searchByOrder) || (rep.orderMaterialCode || '').includes(searchByOrder))
  }, [ searchByOrder, filteredTypeReceipts ])

  filteredReceipts.sort((a, b) => a.updatedAt!.localeCompare(b.updatedAt!))
  return filteredReceipts
}

const getNextApprover = (department?: Department) : Department | undefined => {
  if (department === Department.PRODUCTION) return Department.QC
  if (department === Department.QC) return Department.WAREHOUSE
  if (department === Department.WAREHOUSE) return Department.FA
  return department
}